import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [serverWaking, setServerWaking] = useState(false);

  const checkServerHealth = useCallback(async (retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        await api.get('/health', { timeout: 5000 });
        return true;
      } catch (error) {
        if (i < retries - 1) {
          setServerWaking(true);
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    return false;
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      setPermissionsLoading(true);
      setPermissionsError(false);
      const res = await api.get('/permissions/my-permissions');
      setPermissions(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissionsError(true);
      // API hatası durumunda null döndür - canAccessModule deny-by-default yapacak
      setPermissions(null);
      return null;
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  // Check if user has permission for a module and action
  const hasPermission = useCallback((module, action = 'view') => {
    // Admin her zaman erişebilir (rol bazlı güvenlik tabanı)
    if (user?.role === 'admin') return true;
    
    // Permissions yükleniyorsa veya hata varsa - deny by default
    if (permissionsLoading || permissionsError || !permissions) return false;
    
    const modulePerms = permissions.permissions?.[module];
    if (!modulePerms) return false;
    
    return modulePerms.actions?.includes(action) || false;
  }, [permissions, permissionsLoading, permissionsError, user]);

  // Check if module is accessible (for sidebar visibility)
  const canAccessModule = useCallback((module) => {
    // Admin her zaman erişebilir (rol bazlı güvenlik tabanı)
    if (user?.role === 'admin') return true;
    
    // Permissions yükleniyorsa - varsayılan olarak göster (flash engellemek için)
    // Ama permissions hata verdiyse - deny by default
    if (permissionsError) return false;
    if (permissionsLoading || !permissions) return true; // Loading sırasında flash engelleme
    
    return permissions.accessible_modules?.includes(module) || false;
  }, [permissions, permissionsLoading, permissionsError, user]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      // First check if server is ready
      await checkServerHealth();
      setServerWaking(false);
      
      if (token && savedUser) {
        setUser(JSON.parse(savedUser));
        // Verify token is still valid
        authAPI.me()
          .then(async (res) => {
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
            // Fetch user permissions
            await fetchPermissions();
          })
          .catch(() => {
            logout();
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    };
    
    initAuth();
  }, [checkServerHealth, fetchPermissions]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch permissions after login
    await fetchPermissions();
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await authAPI.register({ name, email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch permissions after register
    await fetchPermissions();
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions(null);
  };

  // Refresh permissions (can be called after permission changes)
  const refreshPermissions = async () => {
    return await fetchPermissions();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      permissions,
      permissionsLoading,
      permissionsError,
      loading, 
      serverWaking, 
      login, 
      register, 
      logout,
      hasPermission,
      canAccessModule,
      refreshPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
